import { BankAccount, BankAccountFormData } from '@/types';
import { v4 as uuidv4 } from 'uuid';
import { 
  saveAccountToSupabase, 
  getOrCreateOthersCategory
} from '@/lib/supabase-operations';
import { deleteAccountWithRelatedData } from '@/lib/supabase-account-cascade';
import { logger } from '@/utils/logger';
import { AccountsAction } from './reducer';

export async function addAccountOperation(
  account: BankAccountFormData,
  dispatch: React.Dispatch<AccountsAction>,
  maxAccounts: number | "unlimited",
  currentAccountCount: number
): Promise<BankAccount> {
  // Enforce subscription limits
  if (typeof maxAccounts === 'number' && currentAccountCount >= maxAccounts) {
    throw new Error(`Seu plano permite no máximo ${maxAccounts} conta(s) bancária(s). Atualize seu plano para adicionar mais.`);
  }

  const newAccount: BankAccount = {
    id: uuidv4(),
    ...account,
    balance: account.balance || 0,
  };
  
  try {
    await saveAccountToSupabase(newAccount);
    dispatch({ type: 'ADD_ACCOUNT', payload: newAccount });

    // Create initial balance transaction if needed
    if (newAccount.balance !== 0) {
      logger.debug('Account created with initial balance, creating transaction...', {
        accountId: newAccount.id,
        balance: newAccount.balance
      });
      // Use async without awaiting to not block account creation
      createInitialBalanceTransaction(newAccount.id, newAccount.balance).catch(err => {
        logger.error('Failed to create initial balance transaction', { error: err });
      });
    }

    logger.info('Account added successfully', { name: newAccount.name });
    return newAccount;
  } catch (error) {
    logger.error('Error adding account', { error });
    throw error;
  }
}

export async function updateAccountOperation(
  id: string,
  updates: Partial<BankAccount>,
  accounts: BankAccount[],
  dispatch: React.Dispatch<AccountsAction>
): Promise<void> {
  try {
    const existingAccount = accounts.find(a => a.id === id);
    if (!existingAccount) throw new Error('Conta não encontrada');

    const updatedAccount = { ...existingAccount, ...updates };
    
    await saveAccountToSupabase(updatedAccount);
    dispatch({ type: 'UPDATE_ACCOUNT', payload: { id, updates } });

    logger.info('Account updated successfully', { id });
  } catch (error) {
    logger.error('Error updating account', { error, id });
    throw error;
  }
}

export async function updateAccountBalanceOperation(
  accountId: string,
  amount: number,
  accounts: BankAccount[],
  dispatch: React.Dispatch<AccountsAction>
): Promise<void> {
  try {
    const account = accounts.find(a => a.id === accountId);
    if (!account) {
      throw new Error('Conta não encontrada');
    }

    const newBalance = account.balance + amount;
    const updatedAccount = { ...account, balance: newBalance };
    
    await saveAccountToSupabase(updatedAccount);
    dispatch({ type: 'UPDATE_ACCOUNT', payload: { id: accountId, updates: { balance: newBalance } } });

    logger.info('Account balance updated successfully', { accountId, newBalance });
  } catch (error) {
    logger.error('Error updating account balance', { error, accountId });
    throw error;
  }
}

export async function deleteAccountOperation(
  id: string,
  dispatch: React.Dispatch<AccountsAction>
): Promise<void> {
  try {
    // Use cascade delete to remove account and all related data
    const deletedCounts = await deleteAccountWithRelatedData(id);
    dispatch({ type: 'DELETE_ACCOUNT', payload: id });

    logger.info('Account deleted with related data', { id, deletedCounts });

    // Emit global event to notify other contexts about the deletion
    window.dispatchEvent(new CustomEvent('account-deleted', {
      detail: { accountId: id, deletedCounts }
    }));
    logger.debug('Account deletion event dispatched', { accountId: id });
  } catch (error: any) {
    logger.error('Error deleting account', { error, id });
    // Re-throw with a user-friendly message
    throw new Error(error.message || 'Erro ao excluir a conta. Tente novamente.');
  }
}

async function createInitialBalanceTransaction(accountId: string, initialBalance: number) {
  if (initialBalance === 0) {
    logger.debug('Initial balance is 0, skipping transaction creation');
    return;
  }
  
  logger.debug('Creating initial balance transaction', { accountId, initialBalance });
  
  const transactionType = initialBalance > 0 ? 'receita' as const : 'despesa' as const;
  
  // Fetch or create the "Outros" category from the database
  const categoryId = await getOrCreateOthersCategory(transactionType);
  
  if (!categoryId) {
    logger.error('Could not find or create "Outros" category', { type: transactionType });
    return;
  }
  
  logger.debug('Found/created category for initial balance', { categoryId, type: transactionType });
  
  const today = new Date();
  const localDate = today.getFullYear() + '-' + 
    String(today.getMonth() + 1).padStart(2, '0') + '-' + 
    String(today.getDate()).padStart(2, '0');
  
  const transactionData = {
    type: transactionType,
    amount: Math.abs(initialBalance),
    description: 'Saldo inicial',
    categoryId: categoryId,
    date: localDate,
    accountId: accountId,
    paymentMethod: 'dinheiro' as const,
    status: 'paid' as const,
    isInitialBalance: true
  };

  logger.debug('Transaction data to dispatch', { transactionData });

  setTimeout(() => {
    window.dispatchEvent(new CustomEvent('create-initial-balance-transaction', {
      detail: transactionData
    }));
    logger.debug('Initial balance transaction event dispatched');
  }, 100);
}
